import Papa from 'papaparse'
import type { ImportAzureDevOpsWorkItemRow } from '@/types'

export const MAX_FILE_BYTES = 5 * 1024 * 1024
export const MAX_ROWS = 500
export const TITLE_MAX = 256
export const DESCRIPTION_MAX = 50000
export const EPIC_FEATURE_MAX = 256


export interface ProcessTypeSets {
  leaf: Set<string>
  epic: Set<string>
  feature: Set<string>
}

export const DEFAULT_TYPE_SETS: ProcessTypeSets = {
  leaf: new Set(['product backlog item', 'user story', 'bug']),
  epic: new Set(['epic']),
  feature: new Set(['feature']),
}

export function buildTypeSets(config?: {
  requirementTypes?: string[]
  epicTypes?: string[]
  featureTypes?: string[]
}): ProcessTypeSets {
  if (!config) return DEFAULT_TYPE_SETS

  const toSet = (values: string[] | undefined, fallback: Set<string>) =>
    values && values.length > 0 ? new Set(values.map(v => v.trim().toLowerCase())) : fallback

  return {
    leaf: toSet(config.requirementTypes, DEFAULT_TYPE_SETS.leaf),
    epic: toSet(config.epicTypes, DEFAULT_TYPE_SETS.epic),
    feature: config.featureTypes
      ? new Set(config.featureTypes.map(v => v.trim().toLowerCase()))
      : DEFAULT_TYPE_SETS.feature,
  }
}

export interface PreviewRow extends ImportAzureDevOpsWorkItemRow {
  key: string
}

interface ParsedColumns {
  id: string | undefined
  title: string | undefined
  title1: string | undefined
  title2: string | undefined
  title3: string | undefined
  workItemType: string | undefined
  description: string | undefined
  acceptanceCriteria: string | undefined
}

const COLUMN_ALIASES: Record<keyof ParsedColumns, readonly string[]> = {
  id: ['id', 'work item id'],
  title: ['title'],
  title1: ['title 1'],
  title2: ['title 2'],
  title3: ['title 3'],
  workItemType: ['work item type'],
  description: ['description'],
  acceptanceCriteria: ['acceptance criteria', 'acceptancecriteria'],
}

interface ParseResult {
  rows: PreviewRow[]
  error: string | null
  isTreeFormat: boolean
}

export type ParseOutcome =
  | { kind: 'error'; message: string }
  | { kind: 'success'; rows: PreviewRow[]; isTreeFormat: boolean }

function resolveColumns(headers: string[]): ParsedColumns {
  const normalized = headers.map(h => ({ raw: h, lower: h.trim().toLowerCase() }))
  const result: ParsedColumns = {
    id: undefined,
    title: undefined,
    title1: undefined,
    title2: undefined,
    title3: undefined,
    workItemType: undefined,
    description: undefined,
    acceptanceCriteria: undefined,
  }
  for (const key of Object.keys(COLUMN_ALIASES) as (keyof ParsedColumns)[]) {
    const aliases = COLUMN_ALIASES[key]
    const match = normalized.find(h => aliases.includes(h.lower))
    if (match) result[key] = match.raw
  }
  return result
}

function buildDescription(r: Record<string, string>, cols: ParsedColumns): string {
  let description = cols.description ? (r[cols.description] ?? '').trim() : ''
  const acceptanceCriteria = cols.acceptanceCriteria ? (r[cols.acceptanceCriteria] ?? '').trim() : ''
  if (acceptanceCriteria) {
    description = description
      ? `${description}\n\n## Acceptance Criteria\n${acceptanceCriteria}`
      : `## Acceptance Criteria\n${acceptanceCriteria}`
  }
  return description
}

function parseAzureDevOpsId(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const parsed = Number.parseInt(trimmed, 10)
  if (Number.isNaN(parsed) || parsed <= 0) return null
  return parsed
}

function buildFlatRows(rows: Record<string, string>[], cols: ParsedColumns): ParseResult {
  const result: PreviewRow[] = []

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]
    const title = ((cols.title && r[cols.title]) ?? '').trim()
    if (!title) return { rows: [], error: `Row ${i + 1}: Title is empty.`, isTreeFormat: false }
    if (title.length > TITLE_MAX) return { rows: [], error: `Row ${i + 1}: Title exceeds ${TITLE_MAX} characters.`, isTreeFormat: false }

    const description = buildDescription(r, cols)
    if (description.length > DESCRIPTION_MAX) return { rows: [], error: `Row ${i + 1}: Description exceeds ${DESCRIPTION_MAX} characters.`, isTreeFormat: false }

    const rawId = cols.id ? (r[cols.id] ?? '') : ''
    const azureDevOpsId = parseAzureDevOpsId(rawId)
    if (azureDevOpsId === null) return { rows: [], error: `Row ${i + 1}: Azure DevOps ID is required and must be a positive integer.`, isTreeFormat: false }

    result.push({
      key: `row-${i}`,
      azureDevOpsId,
      title,
      description: description || null,
      epic: null,
      feature: null,
    })
  }

  return { rows: result, error: null, isTreeFormat: false }
}

function buildTreeRows(rows: Record<string, string>[], cols: ParsedColumns, typeSets: ProcessTypeSets): ParseResult {
  const result: PreviewRow[] = []

  let currentEpic: string | null = null
  let currentFeature: string | null = null

  const getColValue = (r: Record<string, string>, col: string | undefined): string =>
    col ? (r[col] ?? '').trim() : ''

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]
    const type = getColValue(r, cols.workItemType).toLowerCase()
    const t1 = getColValue(r, cols.title1)
    const t2 = getColValue(r, cols.title2)
    const t3 = getColValue(r, cols.title3)

    const title = t3 || t2 || t1
    const titleLevel = t3 ? 3 : t2 ? 2 : t1 ? 1 : 0

    if (!title || titleLevel === 0) {
      return { rows: [], error: `Row ${i + 1}: Title is empty.`, isTreeFormat: true }
    }

    if (typeSets.epic.has(type)) {
      currentEpic = title
      currentFeature = null
      continue
    }
    if (typeSets.feature.has(type)) {
      currentFeature = title
      continue
    }
    if (!typeSets.leaf.has(type)) {
      continue
    }

    if (title.length > TITLE_MAX) {
      return { rows: [], error: `Row ${i + 1}: Title exceeds ${TITLE_MAX} characters.`, isTreeFormat: true }
    }

    const description = buildDescription(r, cols)
    if (description.length > DESCRIPTION_MAX) {
      return { rows: [], error: `Row ${i + 1}: Description exceeds ${DESCRIPTION_MAX} characters.`, isTreeFormat: true }
    }

    const rawId = cols.id ? (r[cols.id] ?? '') : ''
    const azureDevOpsId = parseAzureDevOpsId(rawId)
    if (azureDevOpsId === null) {
      return { rows: [], error: `Row ${i + 1}: Azure DevOps ID is required and must be a positive integer.`, isTreeFormat: true }
    }

    const rowEpic = titleLevel > 1 ? currentEpic : null
    const rowFeature = titleLevel > 2 ? currentFeature : null

    if (rowEpic && rowEpic.length > EPIC_FEATURE_MAX) {
      return { rows: [], error: `Row ${i + 1}: Epic exceeds ${EPIC_FEATURE_MAX} characters.`, isTreeFormat: true }
    }
    if (rowFeature && rowFeature.length > EPIC_FEATURE_MAX) {
      return { rows: [], error: `Row ${i + 1}: Feature exceeds ${EPIC_FEATURE_MAX} characters.`, isTreeFormat: true }
    }

    result.push({
      key: `row-${i}`,
      azureDevOpsId,
      title,
      description: description || null,
      epic: rowEpic,
      feature: rowFeature,
    })
  }

  return { rows: result, error: null, isTreeFormat: true }
}

function validateFile(file: File): string | null {
  if (!file.name.toLowerCase().endsWith('.csv')) return 'Only CSV files are supported.'
  if (file.size > MAX_FILE_BYTES) return `File exceeds the ${MAX_FILE_BYTES / (1024 * 1024)} MB limit.`

  return null
}

function processParsedCsv(results: Papa.ParseResult<Record<string, string>>, typeSets: ProcessTypeSets): ParseOutcome {
  if (results.errors.length > 0) {
    const first = results.errors[0]

    return { kind: 'error', message: `Failed to parse CSV (line ${(first.row ?? 0) + 1}): ${first.message}` }
  }

  const headers = results.meta.fields ?? []
  const cols = resolveColumns(headers)

  if (!cols.id) return { kind: 'error', message: 'Missing required column: ID.' }

  const treeFormat = Boolean(cols.title1)
  if (!treeFormat && !cols.title) {
    return { kind: 'error', message: 'Missing required column: Title (or Title 1 for hierarchical exports).' }
  }
  if (treeFormat && !cols.workItemType) {
    return { kind: 'error', message: 'Hierarchical exports require a Work Item Type column.' }
  }

  const dataRows = results.data
  if (dataRows.length === 0) return { kind: 'error', message: 'No data rows found.' }
  if (dataRows.length > MAX_ROWS) {
    return { kind: 'error', message: `File has ${dataRows.length} rows; the maximum is ${MAX_ROWS}.` }
  }

  const built = treeFormat ? buildTreeRows(dataRows, cols, typeSets) : buildFlatRows(dataRows, cols)
  if (built.error) return { kind: 'error', message: built.error }
  if (built.rows.length === 0) {
    return {
      kind: 'error',
      message: treeFormat
        ? 'No importable work items found in the CSV for this project’s Azure DevOps process.'
        : 'No data rows found.',
    }
  }

  return { kind: 'success', rows: built.rows, isTreeFormat: built.isTreeFormat }
}

export function parseCsvFile(file: File, typeSets: ProcessTypeSets = DEFAULT_TYPE_SETS): Promise<ParseOutcome> {
  const validationError = validateFile(file)
  if (validationError) return Promise.resolve({ kind: 'error', message: validationError })

  return new Promise<ParseOutcome>((resolve) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => resolve(processParsedCsv(results, typeSets)),
      error: (err) => resolve({ kind: 'error', message: `Failed to read CSV: ${err.message}` }),
    })
  })
}
